/** @jsx jsx */
import { h } from 'preact'
import { useMemo, useEffect, useRef } from 'preact/hooks'

import { observer } from 'mobx-react-lite'
import { jsx, css } from '@emotion/react'
import classnames from 'classnames'

import { getContext } from "@searchspring/snap-toolbox";


import {
    Banner,
    CacheProvider,
    InlineBanner,
    useTheme,
    useDisplaySettings,
} from '@searchspring/snap-preact-components'

import { ButtonPagination } from '../Pagination/ButtonPagination'
import { Result } from '../Result/Result'
import { Article } from '../Result/Article'
import { ArrowLeft } from '../Icons/ArrowLeft'

export function defined(properties) {
    const definedProps = {}

    Object.keys(properties).map(key => {
        if (properties[key] !== undefined) {
            definedProps[key] = properties[key]
        }
    })

    return definedProps
}

const CSS = {
    results: ({ columns, gapSize }) =>
        css({
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gridTemplateRows: 'auto',
            gap: gapSize,
        }),
}

const BannerType = {
    HEADER: 'header',
    FOOTER: 'footer',
    LEFT: 'left',
    BANNER: 'banner',
    INLINE: 'inline',
}

const Layout = {
    GRID: 'grid',
    LIST: 'list',
}

const defaultBreakpointsProps = {
    0: {
        columns: 1,
    },
    370: {
        columns: 2,
    },
    990: {
        columns: 3,
    },
    1300: {
        columns: 4,
    },
}

export const ResultsComponent = observer(properties => {
    const globalTheme = useTheme()

    let props = {
        // default props
        results: properties.controller?.store?.results,
        columns: 4,
        gapSize: '20px',
        layout: Layout.GRID,
        breakpoints: defaultBreakpointsProps,
        // global theme
        ...globalTheme?.components?.results,
        // props
        ...properties,
        ...properties.theme?.components?.results,
    }

    const displaySettings = useDisplaySettings(props.breakpoints)
    if (displaySettings && Object.keys(displaySettings).length) {
        props = {
            ...props,
            ...displaySettings,
        }
    }

    const { disableStyles, className, layout, style, controller } = props
    const {
        store: { merchandising },
    } = controller

    const subProps = {
        result: {
            // default props
            className: 'ss__results__result',
            // global theme
            ...globalTheme?.components?.result,
            // inherited props
            ...defined({
                disableStyles,
            }),
            // component theme overrides
            theme: props.theme,
        },
        inlineBanner: {
            // default props
            className: 'ss__results__inline-banner',
            // global theme
            ...globalTheme?.components?.inlineBanner,
            // inherited props
            ...defined({
                disableStyles,
            }),
            // component theme overrides
            theme: props.theme,
        },
    }

    let results
    if (props?.columns > 0 && props?.rows > 0) {
        results = props.results.slice(0, props.columns * props.rows)
    } else {
        results = props.results
    }

    const styling = {}
    if (!disableStyles) {
        styling.css = [
            CSS.results({
                columns: layout === Layout.LIST ? 1 : props.columns,
                gapSize: props.gapSize,
            }),
            style,
        ]
    } else if (style) {
        styling.css = [style]
    }

    const pagination = controller.store.pagination
    const featuredSwatchObj = controller.store.config?.globals?.featuredSwatchObj

    const previousResultsCountRef = useRef(results?.length || 0)
    const previousPageNumberRef = useRef(pagination?.current?.number || 0)
    const previousControllerIdRef = useRef(controller?.store?.config?.id)
    const loadMoreButtonRef = useRef(null)

    useEffect(() => {
        const currentControllerId = controller?.store?.config?.id
        const previousControllerId = previousControllerIdRef.current

        if (currentControllerId !== previousControllerId) {
            previousControllerIdRef.current = currentControllerId
            previousResultsCountRef.current = results?.length || 0
            previousPageNumberRef.current = pagination?.current?.number || 0
            return
        }

        const currentResultsCount = results?.length || 0
        const currentPageNumber = pagination?.current?.number || 0
        const previousResultsCount = previousResultsCountRef.current
        const previousPageNumber = previousPageNumberRef.current

        const resultsIncreased = currentResultsCount > previousResultsCount
        const pageChanged = currentPageNumber > previousPageNumber

        if ((resultsIncreased || pageChanged) && previousResultsCount > 0) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const firstNewResultElement = document.querySelector('[data-first-new-result="true"]')
                    
                    if (firstNewResultElement) {
                        const firstNewLink = firstNewResultElement.querySelector('a.ss__result-link')
                        
                        if (firstNewLink) {
                            firstNewResultElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

                            requestAnimationFrame(() => {
                                firstNewLink.focus({ preventScroll: false })
                                firstNewResultElement.removeAttribute('data-first-new-result')
                            })
                        } else {
                            firstNewResultElement.removeAttribute('data-first-new-result')
                        }
                    }
                })
            })
        }

        previousResultsCountRef.current = currentResultsCount
        previousPageNumberRef.current = currentPageNumber
    }, [controller?.store?.config?.id, results?.length, pagination?.current?.number])

    const context = getContext(['template']);

    if (context?.template === 'blog.magazine-issues') {
      const data = [];
      results.map(result => {
          const magIssueNumber = result?.attributes?.mag_issue_number;
          const title = result?.mappings?.core.name;
          data.push({ id: magIssueNumber, title: title });
        }    
      )
      localStorage.setItem('issue_titles', JSON.stringify(data));
    }

    return results?.length ? (
        <CacheProvider>
            <div {...styling} className={classnames('ss__results', className)}>
            { context?.template === 'blog.magazine' ? <div className="ss__mag-back-link"><ArrowLeft/><a href="/blogs/magazine-issues">{window.Resources.searchspring.issue.backToHome}</a></div> : null}
                {results.map((result, index) =>
                    (() => {
                        const isFirstNewResult = index === previousResultsCountRef.current && index > 0

                        switch (result.type) {
                            case BannerType.BANNER:
                                return (
                                    <InlineBanner
                                        key={result.uid}
                                        {...subProps.inlineBanner}
                                        banner={result}
                                        layout={props.layout}
                                    />
                                )
                            default:
                                return (
                                  controller.store.config?.id === 'articles' ? 
                                    context?.template === 'blog.magazine-issues' ? 
                                      index === 0 ? 
                                        <Article 
                                        result={result}
                                        hideBadge={true}
                                        hideContent={true}
                                        hideContentType={true}
                                        isFirst={true}
                                        index={index}
                                        className="ss__mag-issue-first"
                                        /> :
                                        <Article 
                                        result={result}
                                        hideBadge={true}
                                        hideContent={true}
                                        hideContentType={true}
                                        index={index}
                                        className="ss__mag-issue"
                                        /> :
                                      <Article 
                                      result={result}
                                      index={index}/>
                                     : 
                                      <Result
                                        key={result.uid}
                                        {...subProps.result}
                                        result={result}
                                        layout={props.layout}
                                        controller={controller}
                                        featuredSwatchObj={featuredSwatchObj}
                                        hideWishlist
                                        index={index}
                                        data-first-new-result={isFirstNewResult ? 'true' : undefined}
                                      />
                                ) 
                        }
                    })()
                )}
                 <div class="center load-more-button ss-toolbar-col page-width" ref={loadMoreButtonRef}>
                    {pagination.totalPages > 1 && pagination.current.number !== pagination.last.number && (
                    <ButtonPagination pagination={pagination} />
                    )}
                 </div>
            </div>
            <Banner content={merchandising.content} type="footer" />
        </CacheProvider>
    ) : null
})
